import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, Dialog } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { setBlockModeSwitch } from '../features/contestants/contestantsSlice';
import CountdownTimer from './CountdownTimer';
import BackgroundContainer from './BackgroundContainer';
import BaseButton from './BaseButton';
import DeeperCard from './DeeperCard';
import PopoverCard from './PopoverCard';

const btnSx = {
  whiteSpace: 'nowrap',
  flex: '1 1 0',
  minWidth: 0,
  fontSize: '0.9rem',
  padding: '10px 14px',
};

const formatCustomDuration = (seconds) => {
  if (seconds < 60) return `${seconds} sec`;
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return sec === 0 ? `${min} min` : `${min}:${sec.toString().padStart(2, '0')} min`;
};

const DareQuestionCard = ({ question, formattedTime, questionTimer, drinkQuestion, attributes, swapTurns, swapTurnsAndAddShot, swapSkippedTurns, setPlayerAddShotAndSwap, currentPlayer, playerOne, playerTwo, typeOfDare }) => {
  const playerOneName = playerOne.name;
  const playerTwoName = playerTwo.name;
  const gameMode = useSelector((state) => state.contestants.gameMode);
  const dispatch = useDispatch();

  // Existing state
  const [startCountDown, setStartCountDown] = useState(false);
  const [hideInitialText, setHideInitialText] = useState(false);
  const [hideFinalText, setHideFinalText] = useState(true);
  const [drinkConfirmOpen, setDrinkConfirmOpen] = useState(false);
  const [skippedPopoverOpen, setSkippedPopoverOpen] = useState(false);
  const [tryAgainPopoverOpen, setTryAgainPopoverOpen] = useState(false);
  const [timerKey, setTimerKey] = useState(0);
  const [skippedFromTimer, setSkippedFromTimer] = useState(false);

  // Replay mode skip state
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [showPartnerPicksDare, setShowPartnerPicksDare] = useState(false);
  const [pickerPlayer, setPickerPlayer] = useState(null);
  const [stoppedBy, setStoppedBy] = useState(null);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [customDuration, setCustomDuration] = useState(30);
  const [customTimerActive, setCustomTimerActive] = useState(false);
  const [customTimerDone, setCustomTimerDone] = useState(false);
  const [failedPickerOpen, setFailedPickerOpen] = useState(false);
  const [failedConfirmOpen, setFailedConfirmOpen] = useState(false);
  const [failedPlayer, setFailedPlayer] = useState(null);

  useEffect(() => {
    dispatch(setBlockModeSwitch(showPartnerPicksDare));
    return () => dispatch(setBlockModeSwitch(false));
  }, [showPartnerPicksDare]);

  const restartTimer = () => {
    setSkippedPopoverOpen(false);
    setTryAgainPopoverOpen(false);
    if (skippedFromTimer) {
      setHideFinalText(true);
      setTimerKey(k => k + 1);
    }
  };

  const completetaskwithouttimer = () => {
    setHideInitialText(true);
    setHideFinalText(false);
  };

  const handleCountdownOver = () => { setHideFinalText(false); };

  const toggleTimer = () => {
    setHideFinalText(true);
    setHideInitialText(true);
    setStartCountDown(true);
  };

  const handleSkipConfirm = () => {
    setSkipConfirmOpen(false);
    setStoppedBy(currentPlayer.id);
    setPickerPlayer(currentPlayer === playerOne ? playerTwo : playerOne);
    setShowPartnerPicksDare(true);
  };

  const handleReplayGiveUp = (picker) => {
    setStoppedBy(currentPlayer.id);
    setPickerPlayer(picker);
    setShowPartnerPicksDare(true);
  };

  const handleFailedPlayerPicked = (player) => {
    setFailedPlayer(player);
    setFailedPickerOpen(false);
    setFailedConfirmOpen(true);
  };

  const handleFailedConfirm = () => {
    setFailedConfirmOpen(false);
    setStoppedBy(failedPlayer.id);
    setPickerPlayer(failedPlayer === playerOne ? playerTwo : playerOne);
    setShowPartnerPicksDare(true);
  };

  const handleCustomTimerOver = () => {
    setCustomTimerDone(true);
  };

  const handleCustomTimerRestart = () => {
    setCustomTimerDone(false);
  };

  if (showPartnerPicksDare) {
    return (
      <BackgroundContainer>
        {customTimerActive ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1}>
            <CountdownTimer
              duration={customDuration}
              toggleTimer={handleCustomTimerRestart}
              handleCountdownOver={handleCustomTimerOver}
              onGiveUp={setPlayerAddShotAndSwap}
              playerOne={playerOne}
              playerTwo={playerTwo}
              hideGiveUp={true}
            />
            {customTimerDone && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '-70px' }}>
                <Typography style={{ fontFamily: 'Playfair Display, serif', color: '#d48aae', fontSize: 28, fontWeight: 'bold', letterSpacing: '0.08em', textShadow: '0px 4px 6px rgba(0,0,0,0.4)' }}>
                  TIME'S UP!
                </Typography>
                <BaseButton onClick={() => swapSkippedTurns(stoppedBy)}>Next!</BaseButton>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', paddingTop: '50px' }}>
            <DeeperCard
              typeOfDare={typeOfDare}
              question={`${pickerPlayer?.name ?? 'Your partner'} can pick any previous dare from the game, and you have to do it for double the time!`}
              attributes={[]}
            />
            <BaseButton sx={{ marginTop: '20px' }} onClick={() => setTimePickerOpen(true)}>
              Set The Time
            </BaseButton>
          </Box>
        )}

        <Dialog
          open={timePickerOpen}
          onClose={() => setTimePickerOpen(false)}
          PaperProps={{ style: { background: 'none', boxShadow: 'none', borderRadius: 0 } }}
        >
          <Box sx={{
            padding: '28px 24px',
            background: `
              radial-gradient(ellipse at 20% 10%, rgba(89, 18, 51, 0.22) 0%, transparent 52%),
              radial-gradient(ellipse at 80% 90%, rgba(64, 13, 41, 0.18) 0%, transparent 48%),
              rgba(26, 8, 20, 0.95)
            `,
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(89, 18, 51, 0.22)',
            minWidth: '260px',
            maxWidth: 'min(90vw, 360px)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}>
            <Typography sx={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', color: '#d48aae', textAlign: 'center' }}>
              Set the time
            </Typography>
            <Typography sx={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: '#e8c0d8', letterSpacing: '0.04em' }}>
              {formatCustomDuration(customDuration)}
            </Typography>
            <Slider
              value={customDuration}
              min={30}
              max={600}
              step={30}
              marks={false}
              onChange={(_, val) => setCustomDuration(val)}
              sx={{
                width: '85%',
                color: '#C5449C',
                '& .MuiSlider-thumb': { backgroundColor: '#e8c0d8' },
                '& .MuiSlider-markLabel': {
                  color: '#a07888',
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '0.8rem',
                },
                '& .MuiSlider-mark': { backgroundColor: 'rgba(205, 80, 150, 0.5)' },
              }}
            />
            <BaseButton onClick={() => { setTimePickerOpen(false); setCustomTimerActive(true); }}>
              Start!
            </BaseButton>
          </Box>
        </Dialog>
      </BackgroundContainer>
    );
  }

  return (
    <BackgroundContainer>
      <Box display={'flex'} justifyContent={'flex-end'} flexGrow={1}>
        {startCountDown && (
          <CountdownTimer
            key={timerKey}
            duration={questionTimer}
            toggleTimer={toggleTimer}
            handleCountdownOver={handleCountdownOver}
            onGiveUp={setPlayerAddShotAndSwap}
            playerOne={playerOne}
            playerTwo={playerTwo}
            gameMode={gameMode}
            onGiveUpReplay={handleReplayGiveUp}
          />
        )}
      </Box>

      {!hideInitialText && (
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '100%', paddingTop: '50px' }}>
          <Box width="100%" maxWidth="500px" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <DeeperCard typeOfDare={typeOfDare} question={question} attributes={attributes} />
            <Box marginTop={'15px'} display="flex" flexDirection="row" justifyContent="center" alignItems="center" gap="8px" width="100%">
              {gameMode === 'drink' ? (
                <BaseButton sx={btnSx} onClick={() => setDrinkConfirmOpen(true)}>Drink Instead</BaseButton>
              ) : (
                <BaseButton sx={btnSx} onClick={() => setSkipConfirmOpen(true)}>Skip Dare</BaseButton>
              )}
              {questionTimer > 0 ? (
                <BaseButton sx={btnSx} onClick={toggleTimer}>({formattedTime}) Start</BaseButton>
              ) : (
                <BaseButton sx={btnSx} onClick={completetaskwithouttimer}>Let's Do it!</BaseButton>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <Box display={'flex'} justifyContent={'flex-start'} flexGrow={1} flexBasis={"50px"} flexDirection="column">
        {!hideFinalText && (
          <Box display="flex" justifyContent="flex-start" alignItems="center" flexWrap="wrap">
            {questionTimer > 0 ? (
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                flexDirection="column"
                sx={{ position: 'relative', top: '-90px' }}
              >
                <Typography style={{ fontFamily: 'Playfair Display, serif', color: '#d48aae', fontSize: 28, fontWeight: 'bold', letterSpacing: '0.08em', textShadow: '0px 4px 6px rgba(0,0,0,0.4)', marginBottom: 20 }}>TIME'S UP!</Typography>
                {drinkQuestion ? (
                  <Box display="flex" flexDirection="row" gap="8px" justifyContent="center" alignItems="center">
                    <BaseButton sx={btnSx} onClick={() => setPlayerAddShotAndSwap(playerOne)}>{playerOneName} drank..</BaseButton>
                    <BaseButton sx={btnSx} onClick={() => setPlayerAddShotAndSwap(playerTwo)}>{playerTwoName} drank..</BaseButton>
                    <BaseButton sx={btnSx} onClick={swapTurns}>Nailed it!</BaseButton>
                  </Box>
                ) : (
                  <Box display="flex" flexDirection="row" gap="8px" justifyContent="center" alignItems="center">
                    <BaseButton sx={btnSx} onClick={() => { setSkippedFromTimer(true); if (gameMode === 'replay') { setFailedPickerOpen(true); } else { setSkippedPopoverOpen(true); } }}>Skipped..</BaseButton>
                    <BaseButton sx={btnSx} onClick={swapTurns}>Nailed it!</BaseButton>
                  </Box>
                )}
              </Box>
            ) : (
              <Box display="flex" flexDirection="row" gap="8px" justifyContent="center" alignItems="center">
                <BaseButton sx={btnSx} onClick={() => { setSkippedFromTimer(false); if (gameMode === 'replay') { setFailedPickerOpen(true); } else { setSkippedPopoverOpen(true); } }}>Didn't Do It</BaseButton>
                <BaseButton sx={btnSx} onClick={swapTurns}>Done! Next!</BaseButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <PopoverCard
        open={drinkConfirmOpen}
        onClose={() => setDrinkConfirmOpen(false)}
        mainText="Are you sure you prefer to drink instead of following your dare?"
        showButtonOne={true}
        textButtonOne="Yes, I'll Drink"
        onButtonOne={() => { swapTurnsAndAddShot(); setDrinkConfirmOpen(false); }}
      />
      <PopoverCard
        open={skipConfirmOpen}
        onClose={() => setSkipConfirmOpen(false)}
        mainText="Are you sure you want to skip? Your partner will choose a previous Dare for you! For double the time!"
        showButtonOne={true}
        textButtonOne="Yes, Skip"
        onButtonOne={handleSkipConfirm}
      />
      <PopoverCard
        open={skippedPopoverOpen}
        onClose={() => setSkippedPopoverOpen(false)}
        mainText="If you couldn't finish the task or decided not to do it this time - it means you drink!"
        showButtonOne={true}
        textButtonOne="Try Again"
        onButtonOne={() => setTryAgainPopoverOpen(true)}
        showButtonTwo={true}
        textButtonTwo="Drink"
        onButtonTwo={() => { swapTurnsAndAddShot(); setSkippedPopoverOpen(false); }}
      />
      <PopoverCard
        open={tryAgainPopoverOpen}
        onClose={() => setTryAgainPopoverOpen(false)}
        mainText="Try Again?"
        showButtonOne={true}
        textButtonOne="Yes"
        onButtonOne={restartTimer}
        showButtonTwo={true}
        textButtonTwo="No"
        onButtonTwo={() => setTryAgainPopoverOpen(false)}
        hideBack={true}
      />
      <PopoverCard
        open={failedPickerOpen}
        onClose={() => setFailedPickerOpen(false)}
        mainText="Who failed or decided to stop?"
        showButtonOne={true}
        textButtonOne={playerOne.name}
        onButtonOne={() => handleFailedPlayerPicked(playerOne)}
        showButtonTwo={true}
        textButtonTwo={playerTwo.name}
        onButtonTwo={() => handleFailedPlayerPicked(playerTwo)}
      />
      <PopoverCard
        open={failedConfirmOpen}
        onClose={() => { setFailedConfirmOpen(false); setFailedPickerOpen(true); }}
        mainText={failedPlayer ? `${failedPlayer === playerOne ? playerTwo.name : playerOne.name} will now pick a different dare for you!` : ''}
        showButtonOne={true}
        textButtonOne="Let's Do It!"
        onButtonOne={handleFailedConfirm}
      />
    </BackgroundContainer>
  );
};

export default DareQuestionCard;
